// src/lib/extensionBridge.js — THE Desk↔extension message contract (B244, 2026-07-31)
// ----------------------------------------------------------------------------
// The Desk cannot call the extension directly. A page may only `chrome.runtime
// .sendMessage(<id>, …)` if the manifest allow-lists its origin AND the page knows the
// extension id — and today it cannot, because the extension ships as a zip that agents
// "Load unpacked", so Chrome derives each install's id from ITS FOLDER PATH. Every agent
// therefore has a different id (see BACKLOG B243). Hardcoding one would work on the owner's
// machine and fail silently on everyone else's — the worst failure shape available.
//
// So we use the relay idiom this codebase already runs on every portal (`__sntvRelay__` in
// sentavus_wiring.js, `__sntv_posting_link` in background.js): the page posts a message to
// its OWN window, and a content script the extension injects into this origin picks it up
// and forwards it over chrome.runtime. No id, no allow-list of ids, works on every install.
//
// ⚠️ THIS FILE IS HALF A CONTRACT. The other half is the content script matching
// https://sentavus.vercel.app/* in the extension's manifest. Change a constant here and the
// extension stops answering — with no error, because an unanswered postMessage looks
// exactly like "extension not installed". Change both halves together, always.
//
// ⚠️ SECURITY: we post to `window` with targetOrigin = window.location.origin (never "*"),
// and we IGNORE any inbound message whose event.source is not this window or whose origin
// differs. A page-level message channel is readable by anything else running on the page,
// so nothing secret may travel on it — the reshare payload is listing data the agent is
// about to publish to public portals anyway, which is exactly why it is safe here.
// ⚠️ ONE DELIBERATE EXCEPTION (2026-09-22, office Wi-Fi check): ROUTER_READ carries the router's
// admin password and ROUTER_SAVE the office's reporter token. Both ALREADY EXIST in this page —
// the password is typed into it, the token is returned to it by office_reporter_connect — so
// posting them adds no reader the page did not already have. They go one way (into the
// extension); office_router.js never sends either back.

export const BRIDGE_KEY = '__sntvDesk__'

export const BRIDGE = {
  PING: 'PING',                     // "is the extension there?" → PING_RESP
  RESHARE: 'RESHARE_START',         // repost this payload to one portal → RESHARE_START_RESP
  RESHARE_DRAIN: 'RESHARE_DRAIN',   // fetch reshares the extension stashed while it had no login → _RESP {pending}
  FB_SHARE: 'FB_SHARE_START',       // stage a composed FB post + photos, open facebook.com → _RESP
  // FB pipeline admin controls (owner 2026-09-06). Each relays to the extension background, which
  // RE-CHECKS the server-verified gigi login — the authority — so a non-gigi admin gets not_authorized.
  FB_RUN_SWEEP: 'FB_RUN_SWEEP',     // trigger a group sweep now → _RESP {decisions,...}
  FB_RUN_DM: 'FB_RUN_DM',           // trigger a Messenger read now → _RESP {threads,processed,results}
  FB_GET_CFG: 'FB_GET_CFG',         // read the three switches → _RESP {cfg}
  FB_SET_CFG: 'FB_SET_CFG',         // flip one switch → _RESP {cfg}
  FB_STATUS: 'FB_STATUS',           // switches + daily count + gigi + group count → _RESP
  // Office Wi-Fi check (scheduler 0034): the extension talks to the office router, the page cannot.
  ROUTER_STATUS: 'ROUTER_STATUS',   // is THIS browser an office's reporter → _RESP {connected, officeId, …}
  ROUTER_PROBE: 'ROUTER_PROBE',     // is a GL.iNet router at this private address → _RESP {found, host}
  ROUTER_READ: 'ROUTER_READ',       // log in, read identity + online count → _RESP {router_mac, ssid, model, online}
  ROUTER_SAVE: 'ROUTER_SAVE',       // keep the office's token and start reporting → _RESP {first}
  ROUTER_FORGET: 'ROUTER_FORGET',   // stop reporting here, forget the router password → _RESP
  IMAGE_SEARCH_TAKE: 'IMAGE_SEARCH_TAKE', // the portal photo the agent pressed "Find in Desk" on → _RESP {dataUrl|null}
}

// ⚠️ A PING TIMEOUT IS NOT "NOT INSTALLED" (2026-08-01 — this cost an agent a repost).
// The ping travels page → content script → chrome.runtime → SERVICE WORKER. Under MV3 the
// worker idle-terminates after ~30 seconds, so the first ping after a quiet spell has to
// COLD-START a 6000-line background.js. That routinely takes longer than the old single
// 1200ms budget, and the Desk then rendered "extension not detected" and disabled the repost
// buttons while the extension sat there perfectly healthy — an agent watched it fill a Korter
// form in one window while the other told them to install it.
// So: two attempts, the second with a budget that comfortably covers a cold start. Presence
// itself no longer depends on this round trip at all — see bridgePresence().
const PING_ATTEMPTS_MS = [1500, 6000]
// Reshares open portal tabs and fill long forms with a human watching, so the extension's own
// budget is 15 minutes per platform (handleTransfer's timeoutMs). We are NOT the timeout that
// matters — this only stops a lost message pending forever.
const RESHARE_TIMEOUT_MS = 20 * 60 * 1000

let _seq = 0
const nextId = () => `desk_${Date.now().toString(36)}_${++_seq}`

// One request/response round trip over window.postMessage.
function call(action, data, timeoutMs) {
  return new Promise((resolve, reject) => {
    const reqId = nextId()
    const respAction = `${action}_RESP`
    let done = false

    const onMessage = e => {
      // Same-window, same-origin only — see the SECURITY note in the header.
      if (e.source !== window || e.origin !== window.location.origin) return
      const m = e.data
      if (!m || m[BRIDGE_KEY] !== true || m.action !== respAction || m.reqId !== reqId) return
      cleanup()
      if (m.ok === false) reject(new Error(m.error || 'extension error'))
      else resolve(m)
    }
    const cleanup = () => {
      if (done) return
      done = true
      window.removeEventListener('message', onMessage)
      clearTimeout(timer)
    }
    const timer = setTimeout(() => { cleanup(); reject(new Error('timeout')) }, timeoutMs)

    window.addEventListener('message', onMessage)
    window.postMessage({ [BRIDGE_KEY]: true, action, reqId, ...data }, window.location.origin)
  })
}

// Is the extension INSTALLED? Read synchronously off the DOM, with no message and no service
// worker involved: `desk_bridge.js` stamps `data-sntv-desk-bridge` on <html> as soon as it
// runs, and a content script runs whenever the extension is enabled — asleep worker or not.
//   'ready'  → installed and its bridge is live
//   'stale'  → installed, but THIS TAB's content script was orphaned by an extension reload;
//              only a page refresh restores it (Chrome cannot re-inject into an open tab)
//   null     → no bridge on this page: genuinely not installed (or an older build that
//              predates the marker, which is why callers must still fall back to the ping)
export function bridgePresence() {
  try { return document.documentElement.getAttribute('data-sntv-desk-bridge') } catch { return null }
}

// Ask the extension who it is. Resolves rather than throws — "no extension" is an ordinary
// state the UI renders, not an error.
// `account` is the extension's logged-in agent email, and it matters because the EXTENSION's
// token — not the Desk session — authors everything a repost writes to core. Callers compare
// it to the Desk session and refuse on a proven mismatch; see ShareTab.
//
// The returned shape separates two things the old boolean conflated:
//   present   — the worker answered; the repost path is known good
//   installed — the extension is on this machine, even if the worker did not answer in time
//   stale     — installed but this tab needs a refresh (actionable, and NOT "go install it")
export async function pingExtension() {
  const marker = bridgePresence()
  let lastErr = null
  for (const budget of PING_ATTEMPTS_MS) {
    try {
      const r = await call(BRIDGE.PING, {}, budget)
      // `capabilities` is ADDITIVE and absent on every build shipped so far — see
      // supportsFbShare below for why its absence is the useful signal, not a problem.
      return { present: true, installed: true, stale: false, version: r.version || null,
               account: r.account || null, installId: r.installId || null,
               capabilities: Array.isArray(r.capabilities) ? r.capabilities : null }
    } catch (e) {
      lastErr = e
      // A REPLY that says the context is invalidated is final — retrying cannot fix an
      // orphaned content script, and the second attempt would just cost the agent 6s.
      if (/invalidat|context|Receiving end/i.test(e?.message || '')) break
    }
  }
  const after = bridgePresence()   // re-read: a failed attempt may have downgraded it to 'stale'
  // (capabilities deliberately omitted here — a ping that did not answer proves nothing
  // about what the extension can do, and `null` already means "unknown".)
  const stale = after === 'stale' || /invalidat|context|Receiving end/i.test(lastErr?.message || '')
  return {
    present: false,
    installed: !!after || !!marker,
    stale,
    version: null,
    account: null,
    error: lastErr?.message || null,
  }
}

// Hand one payload to the extension for one portal. Resolves when the extension reports the
// transfer finished; rejects on refusal, error or a lost message.
// `target` is 'korter' | 'ss' | 'myhome' — the same strings handleTransfer's targetSite takes,
// deliberately, so nothing has to translate them on the way through.
export function startReshare(target, payload) {
  return call(BRIDGE.RESHARE, { target, payload }, RESHARE_TIMEOUT_MS)
}

// Drain reshares the extension captured while the agent had NO extension login (Desk-only). Returns
// { pending: [{ _key, at, body }] } of non-secret reshare bodies for the Desk to register with its own
// session. Best-effort + fast: if the extension is asleep/absent, resolve empty rather than blocking.
export async function drainPendingReshares() {
  try { const r = await call(BRIDGE.RESHARE_DRAIN, {}, 4000); return Array.isArray(r?.pending) ? r.pending : [] }
  catch { return [] }
}

// FIND A PORTAL PHOTO IN THE DESK (2026-11-05, extension portal_copy_image.js): the extension keeps the photo the
// agent pressed "Find in Desk" on and opens the Desk; the Desk takes it ONCE here and runs its image search.
// Best-effort: no extension, an older build (the relay drops an unknown action - timeout) or nothing waiting → null.
export async function takePendingImageSearch() {
  try { const r = await call(BRIDGE.IMAGE_SEARCH_TAKE, {}, 6000); return r?.dataUrl ? { dataUrl: r.dataUrl, from: r.from || '' } : null }
  catch { return null }
}

// B894 Phase 4 — hand a composed Facebook post to the extension: it downloads the photos,
// stages the job, opens facebook.com, and fill_facebook.js puts text + photos into the
// Create-post dialog there. The AGENT presses Post. The 2-minute budget covers the photo
// downloads (up to 10, done in the background worker before it answers).
//
// LANGUAGE PER GROUP (owner 2026-08-20). A multi-group spree is ONE publication — one
// sessionId, one dot — so pressing ↗ twice to get Georgian in one group and English in
// another would inflate the count the owner explicitly ruled against. Instead the job
// carries BOTH renders and fill_facebook's banner lets the agent pick per group, at the
// moment they are looking at it. Both texts are already rendered and cached by the time
// this runs, so carrying the second one costs no model call and no extra latency.
//
// ⚠️ ADDITIVE ON PURPOSE — this is half a contract (see the header) and the other half is
// not deployed yet. `text` remains exactly what it always was: the language the agent had
// selected when they pressed. An extension build that has never heard of `texts` keeps
// behaving identically, so the Desk can ship before the extension does. A build that DOES
// read `texts` gets the choice. Neither half has to wait for the other.
//   payload.text          — selected language's text (unchanged contract)
//   payload.texts         — { fb_ka?, fb_en? }, only targets that rendered successfully
//   payload.defaultTarget — which key `text` came from; the banner's initial selection
//
// ⚠️ THE BUDGET STAYS AT TWO MINUTES ON PURPOSE — do not "tidy" it down. The extension
// downloads up to 10 photos BEFORE it answers (see above), and 10 listing photos on a slow
// connection genuinely take longer than any snappy-feeling number. Shortening this does not
// fix the hang it looks like it fixes; supportsFbShare does, by never sending the message to
// a build that cannot answer it.
export function startFacebookShare(payload) {
  return call(BRIDGE.FB_SHARE, { payload }, 2 * 60 * 1000)
}

// Can THIS build stage a Facebook post? (2026-08-20)
//
// The failure this exists to prevent: desk_bridge.js drops an action outside its ALLOWED
// list with NO REPLY AT ALL. So pressing ↗ on a build that predates FB_SHARE_START looks
// exactly like a frozen button for two full minutes, and then says "timeout" — a message
// that tells the agent nothing they can act on. Every zip in the field today is such a
// build, so this is the common case, not an edge case.
//
// A PING cannot answer this by itself: PING is in the ALLOWED list of even the oldest
// build, so it answers cheerfully and proves only that SOMETHING is there. Hence the
// capability list — the extension states what it can do, and silence means "cannot".
//   null / missing → an older build that does not advertise → treat as unsupported
//   []             → advertises, and cannot do this
//   ['fb_share']   → can
// Refusing while unknown is the deliberate direction: the alternative (optimistically
// sending) is precisely the two-minute freeze this replaces. When a build that advertises
// ships, the button lights up on its own — the ping already re-runs on window focus.
export const FB_SHARE_CAPABILITY = 'fb_share'
export function supportsFbShare(ext) {
  return !!ext?.present && Array.isArray(ext.capabilities) && ext.capabilities.includes(FB_SHARE_CAPABILITY)
}

// ── FB PIPELINE ADMIN CONTROLS (owner 2026-09-06) ──────────────────────────────────────────────
// Drive the unattended FB pipeline from the Admin tab. Params ride in `payload` so the relay
// (desk_bridge.js) forwards them to the background verbatim. Every op is gigi-gated in the
// background; a non-gigi extension answers { ok:false, error:'not_authorized' } → these reject.
// A sweep opens tabs and can run minutes; DM reads up to 4 threads — hence the generous budgets.
const FB_SWEEP_TIMEOUT_MS = 8 * 60 * 1000
const FB_DM_TIMEOUT_MS = 5 * 60 * 1000
const FB_CFG_TIMEOUT_MS = 15 * 1000

export function fbRunSweep({ dryRun = false, groups = null } = {}) {
  return call(BRIDGE.FB_RUN_SWEEP, { payload: { dryRun, groups } }, FB_SWEEP_TIMEOUT_MS)
}
export function fbRunDm({ dryRun = false } = {}) {
  return call(BRIDGE.FB_RUN_DM, { payload: { dryRun } }, FB_DM_TIMEOUT_MS)
}
export function fbGetCfg() {
  return call(BRIDGE.FB_GET_CFG, {}, FB_CFG_TIMEOUT_MS)
}
export function fbSetCfg(key, val) {
  return call(BRIDGE.FB_SET_CFG, { payload: { key, val: !!val } }, FB_CFG_TIMEOUT_MS)
}
export function fbStatus() {
  return call(BRIDGE.FB_STATUS, {}, FB_CFG_TIMEOUT_MS)
}

// ── OFFICE ROUTER (scheduler 0034, owner 2026-09-22) ───────────────────────────────────────────
// "Connect to router" in the office panel. A web page cannot reach the office router; the
// extension can, so the panel drives it through these. Same capability rule as fb_share: a build
// that does not ADVERTISE 'office_router' is treated as unable — desk_bridge.js drops an action it
// does not allow with no reply at all, which would read as a frozen button.
export const OFFICE_ROUTER_CAPABILITY = 'office_router'
export function supportsOfficeRouter(ext) {
  return !!ext?.present && Array.isArray(ext.capabilities) && ext.capabilities.includes(OFFICE_ROUTER_CAPABILITY)
}
// READ may cold-start the worker AND compute the router's 5000-round password crypt; SAVE waits
// for the first report. Budgets cover both on a slow machine.
export function routerStatus() { return call(BRIDGE.ROUTER_STATUS, {}, 15 * 1000) }
export function routerProbe(host) { return call(BRIDGE.ROUTER_PROBE, { payload: { host } }, 20 * 1000) }
export function routerRead(host, password) { return call(BRIDGE.ROUTER_READ, { payload: { host, password } }, 60 * 1000) }
export function routerSave(payload) { return call(BRIDGE.ROUTER_SAVE, { payload }, 60 * 1000) }
export function routerForget() { return call(BRIDGE.ROUTER_FORGET, {}, 15 * 1000) }
